import { reactive } from 'vue'
import axios from 'axios'
import { API_ENDPOINTS } from '@/config/api.endpoints'
import type { AuthState } from '@/types/authState'
import type {
  ShopifyPayoutListResponse,
  ShopifyPayoutFilterParams,
  ShopifyPayoutDetail,
  ShopifyBalanceTransactionListResponse,
  ShopifyBalanceTransactionFilterParams,
  ShopifyBalanceTransactionDetail,
  PayoutBankAccountAssignmentRequest,
} from '@/types/shopifyPayouts'
import { normalizeError } from '@/util/errorNormalizer'

export enum API_STATES {
  INITIAL = 'INITIAL',
  LOADING = 'LOADING',
  ERROR = 'ERROR',
  COMPLETE = 'COMPLETE',
}

interface ShopifyPayoutsEmbeddedApi {
  state: API_STATES
  getPayouts(
    authState: AuthState,
    merchantId: string,
    params?: ShopifyPayoutFilterParams,
  ): Promise<ShopifyPayoutListResponse>
  getPayoutDetail(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    businessEntityId: string,
    payoutId: number,
  ): Promise<ShopifyPayoutDetail>
  getBalanceTransactions(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    businessEntityId: string,
    payoutId: number,
    params?: ShopifyBalanceTransactionFilterParams,
  ): Promise<ShopifyBalanceTransactionListResponse>
  getBalanceTransactionDetail(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    businessEntityId: string,
    payoutId: number,
    balanceTransactionId: number,
  ): Promise<ShopifyBalanceTransactionDetail>
  getPayoutsPendingBankAccountAssignment(
    authState: AuthState,
    params?: ShopifyPayoutFilterParams,
  ): Promise<ShopifyPayoutListResponse>
  assignBankAccountToPayout(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    businessEntityId: string,
    payoutId: number,
    request: PayoutBankAccountAssignmentRequest,
  ): Promise<ShopifyPayoutDetail>
  retryBalanceTransaction(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    businessEntityId: string,
    payoutId: number,
    balanceTransactionId: number,
  ): Promise<ShopifyBalanceTransactionDetail>
}

export const shopifyPayoutsEmbeddedApi: ShopifyPayoutsEmbeddedApi = reactive({
  state: API_STATES.INITIAL,

  async getPayouts(
    authState: AuthState,
    merchantId: string,
    params?: ShopifyPayoutFilterParams,
  ): Promise<ShopifyPayoutListResponse> {
    shopifyPayoutsEmbeddedApi.state = API_STATES.LOADING

    const token = authState.user?.accessToken
    const queryParams = new URLSearchParams()
    if (params?.page !== undefined)
      queryParams.append('page', params.page.toString())
    if (params?.size !== undefined)
      queryParams.append('size', params.size.toString())
    // Backend supports comma-separated status values
    if (params?.status && params.status.length > 0) {
      queryParams.append('status', params.status.join(','))
    }
    if (params?.sort) queryParams.append('sort', params.sort)
    if (params?.storeId) queryParams.append('storeId', params.storeId)
    if (params?.businessEntityId)
      queryParams.append('businessEntityId', params.businessEntityId)
    if (params?.bankAccountId)
      queryParams.append('bankAccountId', params.bankAccountId)
    if (params?.id) queryParams.append('id', params.id)

    const queryString = queryParams.toString()
    const url = `${API_ENDPOINTS.SHOPIFY_PAYOUTS_EMBEDDED.PAYOUTS}/${merchantId}/store/business-entity/payout${queryString ? '?' + queryString : ''}`

    return axios
      .get<ShopifyPayoutListResponse>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(({ data }) => {
        shopifyPayoutsEmbeddedApi.state = API_STATES.COMPLETE
        return data
      })
      .catch(err => {
        shopifyPayoutsEmbeddedApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },

  async getPayoutDetail(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    businessEntityId: string,
    payoutId: number,
  ): Promise<ShopifyPayoutDetail> {
    shopifyPayoutsEmbeddedApi.state = API_STATES.LOADING

    const token = authState.user?.accessToken
    const url = `${API_ENDPOINTS.SHOPIFY_PAYOUTS_EMBEDDED.PAYOUTS}/${merchantId}/store/${storeId}/business-entity/${businessEntityId}/payout/${payoutId}`

    return axios
      .get<ShopifyPayoutDetail>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(({ data }) => {
        shopifyPayoutsEmbeddedApi.state = API_STATES.COMPLETE
        return data
      })
      .catch(err => {
        shopifyPayoutsEmbeddedApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },

  async getBalanceTransactions(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    businessEntityId: string,
    payoutId: number,
    params?: ShopifyBalanceTransactionFilterParams,
  ): Promise<ShopifyBalanceTransactionListResponse> {
    shopifyPayoutsEmbeddedApi.state = API_STATES.LOADING

    const token = authState.user?.accessToken
    const queryParams = new URLSearchParams()
    if (params?.page !== undefined)
      queryParams.append('page', params.page.toString())
    if (params?.size !== undefined)
      queryParams.append('size', params.size.toString())
    if (params?.sort) queryParams.append('sort', params.sort)
    if (params?.id) queryParams.append('id', params.id)
    if (params?.processingState)
      queryParams.append('processingState', params.processingState)

    const queryString = queryParams.toString()
    const url = `${API_ENDPOINTS.SHOPIFY_PAYOUTS_EMBEDDED.PAYOUTS}/${merchantId}/store/${storeId}/business-entity/${businessEntityId}/payout/${payoutId}/balance-transaction${queryString ? '?' + queryString : ''}`

    return axios
      .get<ShopifyBalanceTransactionListResponse>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(({ data }) => {
        shopifyPayoutsEmbeddedApi.state = API_STATES.COMPLETE
        return data
      })
      .catch(err => {
        shopifyPayoutsEmbeddedApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },

  async getBalanceTransactionDetail(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    businessEntityId: string,
    payoutId: number,
    balanceTransactionId: number,
  ): Promise<ShopifyBalanceTransactionDetail> {
    shopifyPayoutsEmbeddedApi.state = API_STATES.LOADING

    const token = authState.user?.accessToken
    const url = `${API_ENDPOINTS.SHOPIFY_PAYOUTS_EMBEDDED.PAYOUTS}/${merchantId}/store/${storeId}/business-entity/${businessEntityId}/payout/${payoutId}/balance-transaction/${balanceTransactionId}`

    return axios
      .get<ShopifyBalanceTransactionDetail>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(({ data }) => {
        shopifyPayoutsEmbeddedApi.state = API_STATES.COMPLETE
        return data
      })
      .catch(err => {
        shopifyPayoutsEmbeddedApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },

  async getPayoutsPendingBankAccountAssignment(
    authState: AuthState,
    params?: ShopifyPayoutFilterParams,
  ): Promise<ShopifyPayoutListResponse> {
    shopifyPayoutsEmbeddedApi.state = API_STATES.LOADING

    const token = authState.user?.accessToken
    const queryParams = new URLSearchParams()
    if (params?.page !== undefined)
      queryParams.append('page', params.page.toString())
    if (params?.size !== undefined)
      queryParams.append('size', params.size.toString())
    if (params?.status && params.status.length > 0) {
      queryParams.append('status', params.status.join(','))
    }
    if (params?.sort) queryParams.append('sort', params.sort)
    if (params?.storeId) queryParams.append('storeId', params.storeId)
    if (params?.businessEntityId)
      queryParams.append('businessEntityId', params.businessEntityId)
    if (params?.bankAccountId)
      queryParams.append('bankAccountId', params.bankAccountId)
    if (params?.id) queryParams.append('id', params.id)

    const queryString = queryParams.toString()
    const url = `${API_ENDPOINTS.SHOPIFY_PAYOUTS_EMBEDDED.PENDING_ASSIGNMENT}${queryString ? '?' + queryString : ''}`

    return axios
      .get<ShopifyPayoutListResponse>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(({ data }) => {
        shopifyPayoutsEmbeddedApi.state = API_STATES.COMPLETE
        return data
      })
      .catch(err => {
        shopifyPayoutsEmbeddedApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },

  async assignBankAccountToPayout(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    businessEntityId: string,
    payoutId: number,
    request: PayoutBankAccountAssignmentRequest,
  ): Promise<ShopifyPayoutDetail> {
    shopifyPayoutsEmbeddedApi.state = API_STATES.LOADING

    const token = authState.user?.accessToken
    const url = `${API_ENDPOINTS.SHOPIFY_PAYOUTS_EMBEDDED.PAYOUTS}/${merchantId}/store/${storeId}/business-entity/${businessEntityId}/payout/${payoutId}/bank-account`

    return axios
      .patch<ShopifyPayoutDetail>(url, request, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      .then(({ data }) => {
        shopifyPayoutsEmbeddedApi.state = API_STATES.COMPLETE
        return data
      })
      .catch(err => {
        shopifyPayoutsEmbeddedApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },

  async retryBalanceTransaction(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    businessEntityId: string,
    payoutId: number,
    balanceTransactionId: number,
  ): Promise<ShopifyBalanceTransactionDetail> {
    shopifyPayoutsEmbeddedApi.state = API_STATES.LOADING

    const token = authState.user?.accessToken
    const url = `${API_ENDPOINTS.SHOPIFY_PAYOUTS_EMBEDDED.PAYOUTS}/${merchantId}/store/${storeId}/business-entity/${businessEntityId}/payout/${payoutId}/balance-transaction/${balanceTransactionId}/retry`

    return axios
      .post<ShopifyBalanceTransactionDetail>(url, undefined, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(({ data }) => {
        shopifyPayoutsEmbeddedApi.state = API_STATES.COMPLETE
        return data
      })
      .catch(err => {
        shopifyPayoutsEmbeddedApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },
})
